import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchUserClient,
  GetUserClient,
  DeleteUserClient,
  UpdateUserClient,
  UpdateUserRolesClient,
  CreateUserClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

// API Clients
const searchUserClient = new SearchUserClient(SERVICE_URL)
const getUserClient = new GetUserClient(SERVICE_URL)
const createUserClient = new CreateUserClient(SERVICE_URL)
const deleteUserClient = new DeleteUserClient(SERVICE_URL)
const updateUserClient = new UpdateUserClient(SERVICE_URL)
const updateUserRolesClient = new UpdateUserRolesClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  detail: null,
  excel: null,
  user: null,
}

// Async Thunks
export const userGetAll = createAsyncThunk('user/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchUserClient.search(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setUser(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userExcel = createAsyncThunk('user/excel', async ({params, fileName}, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await searchUserClient.excel(
      params.name,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    if (response && response.data) {
      DownloadFileFn(response.data, fileName)
    }
    dispatch(setExcel(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userGetId = createAsyncThunk('user/getId', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    const response = await getUserClient.get(id)
    dispatch(setDetail(response))
    dispatch(setRedirect(false))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userDelete = createAsyncThunk('user/delete', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await deleteUserClient.delete(params)
    dispatch(setDeleted(true))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userCreate = createAsyncThunk('user/create', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await createUserClient.create(params)
    dispatch(setRedirect(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userUpdate = createAsyncThunk('user/update', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateUserClient.update(params)
    dispatch(setRedirect(response || params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const userRolesUpdate = createAsyncThunk('user/rolesUpdate', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    const response = await updateUserRolesClient.update(params)
    dispatch(setRedirect(response || params))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

// Slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setUser: (state, action) => {
      state.user = action.payload
      state.loading = false
    },
    setDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('user/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

// Export actions
export const {
  setLoading,
  setError,
  setUser,
  setDetail,
  setDeleted,
  setRedirect,
  setArchived,
  setExcel,
  resetState,
} = userSlice.actions

// Selectors
export const selectUserLoading = (state) => state.user.loading
export const selectUserError = (state) => state.user.error
export const selectUser = (state) => state.user.user
export const selectUserDetail = (state) => state.user.detail
export const selectUserDeleted = (state) => state.user.isDeleted
export const selectUserRedirect = (state) => state.user.isRedirect
export const selectUserArchived = (state) => state.user.isArchived
export const selectUserExcel = (state) => state.user.excel

export default userSlice.reducer
